import { cn } from '@skupreme/shared/ui';
import type { BadgeTone } from '@skupreme/shared/ui';

/** The shared Badge tones a status dot comes in. */
export type StatusDotTone =
  | Extract<BadgeTone, 'success' | 'caution' | 'danger' | 'neutral'>
  /* No status to show: a hollow grey ring. */
  | 'unreported';

/* The solid colour of each tone. */
const DOT_TONES: Record<StatusDotTone, string> = {
  success: 'sk:bg-success',
  caution: 'sk:bg-caution',
  danger: 'sk:bg-danger',
  neutral: 'sk:bg-fg-tertiary',
  unreported: 'sk:bg-surface sk:border sk:border-fg-tertiary',
};

/* Caution's orange and danger's red are close at 8 px, so danger is a diamond and every other tone a circle. */
const DOT_SHAPES: Partial<Record<StatusDotTone, string>> = {
  danger: 'sk:rotate-45',
};

/**
 * M2: a small status dot (a diamond for danger, round otherwise), ringed in the surface colour so it reads on a logo in the light and the dark theme.
 * Decorative: the element it marks carries the status in words.
 */
export function StatusDot({
  tone,
  className,
}: {
  tone: StatusDotTone;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      data-tone={tone}
      className={cn(
        'sk:inline-block sk:size-2 sk:shrink-0 sk:ring-2 sk:ring-surface',
        DOT_TONES[tone],
        DOT_SHAPES[tone] ?? 'sk:rounded-full',
        className,
      )}
    />
  );
}
